import React, { useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { BackIcon } from '../../../components/AppHeader';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import { getFlagSrc } from '../../../utils/scoring';

function FlagId({ publicId }) {
  const parts = (publicId || '').split('|');
  const displayId = parts[parts.length - 1];
  const countryCode = parts.length > 1 ? parts[0] : null;
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      {countryCode && (
        <img alt={countryCode} src={getFlagSrc({ countryCode, size: 16 })} style={{ display: 'block' }} />
      )}
      <span>{displayId}</span>
    </Box>
  );
}

export default function ShooterSquadSelect() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { stageIdx } = useParams();
  const { matchId, match, stages, shooters, scores, refreshShooters } = useOutletContext();
  const [dqMessage, setDqMessage] = useState(null);
  const [splitDialog, setSplitDialog] = useState(null);
  const stage = stages[parseInt(stageIdx, 10) - 1];
  const numStages = stages.length;
  const stageIdxNum = parseInt(stageIdx, 10);
  const basePath = `/${matchId}/manage/scores`;

  const latestScores = getLatestScoresPerStage(scores, numStages);
  const shootersDqed = {};
  latestScores.forEach((stageScores, sIdx) => {
    stageScores.forEach((s) => {
      if (s.dq) shootersDqed[s.shooterId] = sIdx + 1;
    });
  });

  const stageScores = latestScores[stageIdxNum - 1] || [];

  const getScoreStatus = (shooterId) => {
    const score = stageScores.find((s) => s.shooterId === shooterId);
    if (!score) return null;
    if (score.dq) return 'dq';
    if (score.signature) return 'ok';
    return 'nosignature';
  };

  const handleShooterSelect = (shooter) => {
    if (!shooter) return;
    if (match.payment !== 'none' && !shooter.paid) return;

    const dqStage = shootersDqed[shooter.id];
    if (dqStage && dqStage !== stageIdxNum) {
      setDqMessage(t('scoring.shooterDisqualifiedWithStage', {
        name: shooter.name, stageIdx: dqStage, dq: '',
      }));
      return;
    }

    const squadId = shooter.squad || '_nosquad';
    const existingScore = stageScores.find((s) => s.shooterId === shooter.id);
    if (existingScore) {
      navigate(`${basePath}/stage/${stageIdx}/squad/${squadId}/score-mode/reviewing/${shooter.id}?fromSearch=1`);
    } else {
      navigate(`${basePath}/stage/${stageIdx}/squad/${squadId}/score-mode/scoring/${shooter.id}?fromSearch=1`);
    }
  };


  const displayShooters = shooters
    .filter((s) => !s.cancelledParticipation)
    .map((s) => (match.payment !== 'none' && !s.paid
      ? { ...s, _displayName: `${s.name} - ${t('manage.unpaid')}`, _unpaid: true }
      : { ...s, _displayName: s.name }));

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(basePath)}>
          {t('common.back')}
        </Button>
        <Typography variant="subtitle1">
          {t('results.stageN', { n: stageIdx })}{stage?.title ? `: ${stage.title}` : ''}
        </Typography>
      </Box>

      <Typography variant="h6" sx={{ textAlign: 'center', mb: 1 }}>{t('scoring.pickShooter')}</Typography>
      <Autocomplete
        options={displayShooters}
        getOptionLabel={(s) => `${s.publicId} ${s._displayName || s.name}`}
        getOptionDisabled={(s) => s._unpaid}
        onChange={(_, shooter) => handleShooterSelect(shooter)}
        renderOption={(props, s) => {
          const { key, ...rest } = props;
          return <li key={key} {...rest}><FlagId publicId={s.publicId} />&nbsp;- {s._displayName || s.name}</li>;
        }}
        renderInput={(params) => <TextField {...params} autoFocus />}
        sx={{ mb: 3 }}
        blurOnSelect
        value={null}
      />

      <Typography variant="h6" sx={{ textAlign: 'center', mb: 1 }}>{t('scoring.pickSquad')}</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {(match?.squads || []).map((sq) => {
          const sqShooters = shooters.filter((s) => s.squad === sq.id && !s.cancelledParticipation);
          const scoredCount = sqShooters.filter((s) => getScoreStatus(s.id)).length;

          return (
            <Button
              key={sq.id}
              variant="contained"
              color="primary"
              fullWidth
              onClick={() => {
                if (stage?.shouldSplit) {
                  setSplitDialog(sq.id);
                } else {
                  navigate(`${basePath}/stage/${stageIdx}/squad/${sq.id}`);
                }
              }}
              sx={{ textTransform: 'none', py: 1.5, justifyContent: 'space-between' }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {scoredCount === sqShooters.length && sqShooters.length > 0 && (
                  <CheckCircleIcon sx={{ fontSize: 20 }} />
                )}
                <Typography variant="body1">{sq.title}</Typography>
              </Box>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                {scoredCount}/{sqShooters.length}
              </Typography>
            </Button>
          );
        })}
      </Box>

      {/* DQ message dialog */}
      <Dialog open={!!dqMessage} onClose={() => setDqMessage(null)}>
        <DialogTitle>{t('scoring.disqualifiedShooterTitle')}</DialogTitle>
        <DialogContent><DialogContentText>{dqMessage}</DialogContentText></DialogContent>
        <DialogActions>
          <Button onClick={() => setDqMessage(null)} color="primary">{t('common.ok')}</Button>
        </DialogActions>
      </Dialog>

      {/* Hot range split dialog */}
      <Dialog open={!!splitDialog} onClose={() => setSplitDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('scoring.hotRangeSplit', 'Hot Range - Select Group')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
            <Button variant="contained" fullWidth onClick={() => { navigate(`${basePath}/stage/${stageIdx}/squad/${splitDialog}`); setSplitDialog(null); }}>
              {t('scoring.splitAll', 'All Shooters')}
            </Button>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="outlined" fullWidth onClick={() => { navigate(`${basePath}/stage/${stageIdx}/squad/${splitDialog}?split=1-2`); setSplitDialog(null); }}>
                {t('scoring.splitFirstHalf', '1st Half')}
              </Button>
              <Button variant="outlined" fullWidth onClick={() => { navigate(`${basePath}/stage/${stageIdx}/squad/${splitDialog}?split=2-2`); setSplitDialog(null); }}>
                {t('scoring.splitSecondHalf', '2nd Half')}
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="outlined" fullWidth onClick={() => { navigate(`${basePath}/stage/${stageIdx}/squad/${splitDialog}?split=1-3`); setSplitDialog(null); }}>
                {t('scoring.splitFirstThird', '1st Third')}
              </Button>
              <Button variant="outlined" fullWidth onClick={() => { navigate(`${basePath}/stage/${stageIdx}/squad/${splitDialog}?split=2-3`); setSplitDialog(null); }}>
                {t('scoring.splitSecondThird', '2nd Third')}
              </Button>
              <Button variant="outlined" fullWidth onClick={() => { navigate(`${basePath}/stage/${stageIdx}/squad/${splitDialog}?split=3-3`); setSplitDialog(null); }}>
                {t('scoring.splitThirdThird', '3rd Third')}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

// Compute latest non-deleted score per shooter per stage
export function getLatestScoresPerStage(scores, numStages) {
  const result = Array.from({ length: numStages }, () => []);
  const grouped = {};

  for (const score of (scores || [])) {
    if (score.deleted) continue;
    const key = `${score.stageIdx}_${score.shooterId}`;
    if (!grouped[key] || (score.timestamp || 0) > (grouped[key].timestamp || 0)) {
      grouped[key] = score;
    }
  }

  for (const score of Object.values(grouped)) {
    const idx = score.stageIdx - 1;
    if (idx >= 0 && idx < numStages) {
      result[idx].push(score);
    }
  }

  return result;
}
